/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { CheckOutlined, CopyOutlined } from '@ant-design/icons';
import { CSSProperties, useState } from 'react';

import { useMessage } from '@/hooks/message';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { Text } from '@/primitives/Text';
import { tokens } from '@/theme';

export type CopyButtonProps = {
  textToCopy: string;
  size?: 'sm' | 'lg';
  style?: CSSProperties;
  failedText?: string;
};

export function CopyButton({ textToCopy, size = 'lg', style, failedText }: CopyButtonProps) {
  const { t } = useMimicTranslation('global');
  const [copied, setCopied] = useState(false);
  const [message] = useMessage();

  const onCopy = () => {
    try {
      navigator.clipboard.writeText(textToCopy);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      message.error(failedText || t('failedCopy'));
    }
  };

  const buttonHeight = size === 'sm' ? '28px' : '32px';
  const buttonStyle: CSSProperties = {
    height: buttonHeight,
    display: 'flex',
    alignItems: 'center',
    borderRadius: '2px',
    ...style,
  };

  if (copied) {
    return (
      <Button type="default" dataTestId="copy-button" style={buttonStyle} onClick={onCopy}>
        <CheckOutlined style={{ fontSize: '12px', color: tokens.color.success.successDefault }} />
        <Text type="default" size={size}>
          {t('copied')}
        </Text>
      </Button>
    );
  }

  return (
    <Button type="default" dataTestId="copy-button" style={buttonStyle} onClick={onCopy}>
      <CopyOutlined style={{ fontSize: '12px' }} />
      <Text type="default" size={size}>
        {t('copy')}
      </Text>
    </Button>
  );
}
